// src/hooks/useMerchantFrontAuth.js
//
// Giriş yapmış merchant kullanıcının rolü + frontAuth yetkilerini AuthContext'ten okuyan
// ve bileşenlerde kullanımı kolay bir API döndüren hook. useFrontAuth'un merchant muadili.
//
// Kullanım:
//   const { can, canView, role, canManagePermissions } = useMerchantFrontAuth();
//   {can("my-operations", "view-all") && ...}

import { useMemo } from "react";
import { useAuth } from "../AuthContext";
import {
  normalizeMerchantRole,
  buildMerchantDefaultPermissions,
  mergeMissingMerchantPagePermissions,
  can as canFn,
  canView as canViewFn,
  canManageMerchantPermissions as canManageMerchantPermissionsFn,
} from "../utils/merchantFrontAuth";

export function useMerchantFrontAuth() {
  const { merchantRole, merchantFrontAuth } = useAuth();

  return useMemo(() => {
    const role = normalizeMerchantRole(merchantRole);

    // Kayıtlı yetki varsa onu, yoksa rol default şablonunu kullan (geriye dönük uyum).
    const hasStored =
      merchantFrontAuth && typeof merchantFrontAuth === "object" && Object.keys(merchantFrontAuth).length > 0;
    // Kayıtlı yetki, sonradan eklenen sayfaların anahtarlarını içermeyebilir → eksikler
    // rol default'undan tamamlanır (açıkça kaldırılmış yetkiler korunur).
    const defaults = buildMerchantDefaultPermissions(role);
    // Tamamlama mantığı tek yerde (merchantFrontAuth.mergeMissingMerchantPagePermissions):
    // sidebar ile rota kapısı aynı sonucu üretsin.
    const permissions = hasStored
      ? mergeMissingMerchantPagePermissions(merchantFrontAuth, role)
      : defaults;

    const can = (pageKey, capability = "view") => canFn(permissions, pageKey, capability);
    const canView = (pageKey) => canViewFn(permissions, pageKey);
    const canManagePermissions = canManageMerchantPermissionsFn(permissions, role);

    return {
      role,
      permissions,
      can,
      canView,
      canManagePermissions,
    };
  }, [merchantRole, merchantFrontAuth]);
}

export default useMerchantFrontAuth;
